<script lang="ts" setup>
import { computed, ref, onMounted } from 'vue';

const props = withDefaults(
  defineProps<{
    width?: string;
    color: string;
    text?: string;
  }>(),
  {
    width: '226',
  },
);

// Compute bubble color based on prop
const bubbleColor = computed(() => {
  return {
    purple: 'text-purple-light',
    yellow: 'text-yellow-lighter',
    green: 'text-green-light',
    blue: 'text-blue-lighter',
  }[props.color];
});

// Create refs for dynamic positioning
const contentRef = ref<HTMLDivElement | null>(null);
const bubbleHeight = ref(240); // Default height

// Ensure content is properly centered after component mounts
onMounted(() => {
  // Add a small delay to ensure the content is fully rendered
  setTimeout(() => {
    if (contentRef.value) {
      // If needed, we could adjust vertical position based on content height
      // This ensures content is truly centered regardless of text amount
    }
  }, 50);
});
</script>

<template>
  <div class="speech-bubble-container" :style="{ width: `${width}px` }">
    <!-- SVG Bubble Shape -->
    <svg
      :width="width"
      :height="bubbleHeight"
      :class="bubbleColor"
      viewBox="0 0 226 240"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      class="speech-bubble-svg"
      preserveAspectRatio="xMidYMid meet"
    >
      <path
        d="M223.81 125.995C221.961 132.826 220.111 139.657 217.424 146.4C214.457 153.113 210.463 158.874 206.689 165.221C203.032 170.456 199.096 175.662 194.69 179.974C189.417 184.477 184.086 189.535 177.593 192.222C175.92 202.726 175.055 213.595 175.615 224.334C175.954 229.148 174.588 234.063 174.089 238.788C170.843 240.131 169.242 236.59 167.435 234.994C160.427 229.195 153.449 223.119 147.396 216.297C143.503 213.075 139.757 208.464 134.542 207.071C132.926 206.338 131.413 207.302 129.709 207.404C119.794 207.762 110.055 206.453 100.448 206.564C91.9582 206.792 84.1284 203.436 76.1226 201.748C68.7043 199.84 62.1083 195.49 55.0571 192.779C48.0353 189.789 41.7184 185.469 35.4015 181.148C32.2283 179.127 30.2889 176.112 27.4536 173.564C22.8406 169.143 20.3573 163.26 16.3318 158.619C12.6441 153.452 9.57327 147.788 7.06062 142.183C5.20925 138.334 5.12047 133.828 3.88596 129.482C2.59278 125.691 2.47466 121.463 0.902369 117.644C0.724808 108.631 1.66363 99.7368 2.60246 90.8424C3.54205 87.2879 4.7901 83.485 5.72969 79.9305C7.09508 75.0157 9.82659 70.5262 11.4711 65.6409C13.4533 60.2292 16.743 55.7986 20.0621 51.09C26.5829 42.7847 33.3534 34.7868 41.9309 28.3849C48.7752 22.3622 56.4273 16.7057 64.5204 12.22C69.25 10.1898 73.4801 7.54477 78.1803 5.7925C87.2724 2.53645 96.8053 0.451122 106.353 0.896827C117.473 -0.177886 129.224 0.781446 141.005 1.46282C146.278 2.3005 151.376 4.80589 156.253 6.72591C160.822 8.89443 165.39 11.063 169.65 13.48C176.526 17.8593 183.401 22.2386 189.571 27.9488C200.192 36.9392 209.801 47.5091 215.208 60.446C218.279 66.1101 219.587 72.4312 221.762 78.5628C223.878 85.2503 223.115 92.4771 224.952 99.1351C225.82 104.286 225.233 109.845 224.955 115.155C224.852 118.798 224.75 122.441 223.81 125.995Z"
        fill="currentColor"
      />
    </svg>

    <!-- Content Container (Positioned Absolutely) -->
    <div class="speech-bubble-content" ref="contentRef">
      <!-- Slot Content -->
      <slot v-if="$slots.default"></slot>
      
      <!-- Text Prop Content -->
      <template v-else-if="text">{{ text }}</template>
    </div>
  </div>
</template>

<style scoped>
.speech-bubble-container {
  position: relative;
  display: inline-block;
}

.speech-bubble-svg {
  display: block;
  width: 100%;
  height: auto;
}

.speech-bubble-content {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 10% 15%;
  /* Account for the bubble's shape to center vertically */
  padding-bottom: 18%; 
  box-sizing: border-box;
  color: #000;
  font-weight: normal;
}

/* Ensure content doesn't overflow the bubble */
.speech-bubble-content :deep(*) {
  max-width: 100%;
}

/* Media Queries to adjust padding for different bubble sizes */
@media (max-width: 480px) {
  .speech-bubble-content {
    padding: 8% 12%;
    padding-bottom: 16%;
  }
}

/* Handle rotation in parent components properly */
:deep(.speech-bubble-container.rotated .speech-bubble-content) {
  transform: rotate(-90deg);
}
</style>